/**
 * Booking-completion detection across scheduler providers.
 *
 * The in-widget booking modal embeds a scheduler iframe (Calendly, HubSpot
 * meetings, …). When the visitor finishes scheduling, the provider posts a
 * `window.postMessage` — that confirmation is the `meeting-booked` GROUND
 * TRUTH (opening the modal ≠ booked). Each provider uses its own message
 * shape, so detection is a small table here rather than one hardcoded check.
 *
 * Adding a provider = one entry below. Detection is shape-based (not URL-based)
 * so it's robust regardless of which URL the operator pasted.
 */

/** A provider's booking-confirmation postMessage signature. */
type BookingSignal = {
  provider: string;
  /** True when this message is the provider's "booking confirmed" event. */
  matches: (data: Record<string, unknown>) => boolean;
};

const SIGNALS: BookingSignal[] = [
  // Calendly fires `{ event: 'calendly.event_scheduled' }`.
  { provider: 'calendly', matches: (d) => d['event'] === 'calendly.event_scheduled' },
  // HubSpot meetings fires `{ meetingBookSucceeded: true }`.
  { provider: 'hubspot', matches: (d) => d['meetingBookSucceeded'] === true },
];

/**
 * Inspect a postMessage payload and return the provider whose
 * booking-confirmation signal it matches, or null. Tolerant of any input
 * (postMessage delivers arbitrary cross-origin data).
 */
export function detectBookingCompletion(data: unknown): { provider: string } | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  for (const sig of SIGNALS) {
    if (sig.matches(d)) return { provider: sig.provider };
  }
  return null;
}
